/**
 * RevokeGrantDialog Component (Story 32.1, AC #14)
 *
 * Confirmation dialog before revoking an entire OAuth grant — the
 * tenant-wide destructive action of the connections tree. Surfaces the
 * blast radius from the server's dual-axis `tenantImpact` counts: every
 * integration whose chain passes through the token, across ALL the
 * tenant's clients, is disconnected.
 *
 * Centered modal Dialog per the ui-design-system directive §4.2/§4.4 —
 * NEVER a side Sheet. Close is blocked while the revoke is in flight.
 * Mount ALWAYS at the call site (§4.6) — the parent keeps a lingering
 * `lastValue` copy of the grant so the exit animation can play.
 */

import { useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { Prohibit } from "@phosphor-icons/react";
import { apiDelete, apiPost } from "../../lib/apiClient";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Types
// ============================================================================

/** The grant the dialog acts on — assembled by ConnectionsTree. */
export interface RevokeGrantTarget {
  oauthTokenId: string;
  /** e.g. "Google account" / "Mailchimp account" — display only. */
  providerLabel: string;
  /** True for api-key platforms — swaps copy to "Remove API key" (AC #13). */
  isApiKey: boolean;
  /**
   * True when the grant's family has no `revocationEndpoint` — the revoke is
   * local-only and the user must also revoke at the provider's dashboard.
   */
  localOnly: boolean;
  /** Tenant-wide dual-axis blast radius (server `tenantImpact`). */
  clientCount: number;
  integrationCount: number;
}

interface RevokeGrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grant: RevokeGrantTarget;
  clientId: string;
  showToast: (type: ToastType, message: string) => void;
}

interface RevokeGrantResponse {
  data: {
    revoked: boolean;
    integrationsDeleted: number;
    clientsAffected: number;
    providerRevoked: boolean;
  };
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

// ============================================================================
// Component
// ============================================================================

export function RevokeGrantDialog({
  open,
  onOpenChange,
  grant,
  clientId,
  showToast,
}: RevokeGrantDialogProps) {
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState(false);

  // §4.2 — never lose in-flight state: block close while the mutation runs.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && revoking) return;
      onOpenChange(next);
    },
    [revoking, onOpenChange]
  );

  const handleRevoke = useCallback(async () => {
    setRevoking(true);
    try {
      const res = await apiDelete(`/api/integrations/grants/${grant.oauthTokenId}`);

      if (!res.ok) {
        const errorJson = await res.json().catch(() => null);
        const detail =
          (errorJson as { detail?: string })?.detail ?? "Failed to revoke the grant";
        showToast("error", detail);
        return;
      }

      const json = (await res.json()) as RevokeGrantResponse;

      if (json.data.providerRevoked || grant.localOnly) {
        showToast(
          "success",
          grant.isApiKey
            ? "API key removed and integrations disconnected"
            : "Grant revoked and integrations disconnected"
        );
      } else {
        // E5 — local delete succeeded, provider call failed: success with caveat.
        showToast(
          "success",
          "Grant removed here, but provider revocation failed — also revoke access from the provider's dashboard"
        );
      }

      // Auto-dismiss notifications for the revoked token (handleDisconnected
      // precedent) — non-fatal.
      await apiPost(`/api/notifications/dismiss-by-token/${grant.oauthTokenId}`).catch(() => {
        // Leftover notifications are harmless
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["client-connections", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-integrations", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["client-integrations-health", clientId] }),
        queryClient.invalidateQueries({ queryKey: ["tenant-google-connections"] }),
        queryClient.invalidateQueries({ queryKey: ["integration-alerts"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-unread-count"] }),
        queryClient.invalidateQueries({ queryKey: ["notification-list"] }),
      ]);

      onOpenChange(false);
    } catch {
      showToast("error", "Failed to revoke the grant. Please try again.");
    } finally {
      setRevoking(false);
    }
  }, [grant, clientId, queryClient, showToast, onOpenChange]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {grant.isApiKey ? "Remove API key?" : "Revoke OAuth grant?"}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {grant.isApiKey
                      ? `Remove the ${grant.providerLabel} and disconnect everything that uses it.`
                      : `Revoke the ${grant.providerLabel} grant and disconnect everything that uses it.`}
                  </DialogPrimitive.Description>
                </div>

                {/* §6 — staggered inner content (50ms increments for forms) */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 }}
                  className="mt-5 text-sm text-foreground"
                >
                  This will permanently disconnect{" "}
                  <strong>
                    {plural(grant.integrationCount, "integration")} across{" "}
                    {plural(grant.clientCount, "client")}
                  </strong>{" "}
                  from {grant.providerLabel}. Reports for those clients will stop
                  receiving data. This cannot be undone.
                </motion.div>

                {grant.localOnly && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="mt-3 text-sm text-muted-foreground"
                  >
                    Your credentials remain valid at the provider — revoke them
                    from the provider&apos;s dashboard as well.
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: grant.localOnly ? 0.15 : 0.1 }}
                >
                  <div className="flex justify-end gap-3 mt-6">
                    <Button
                      variant="outline"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      onClick={() => handleOpenChange(false)}
                      disabled={revoking}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="rounded-2xl text-[13px] cursor-pointer"
                      onClick={handleRevoke}
                      disabled={revoking}
                    >
                      <Prohibit size={16} className="mr-1.5" />
                      {revoking
                        ? "Revoking..."
                        : grant.isApiKey
                          ? "Remove key"
                          : "Revoke grant"}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
